import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SearchIcon from "@mui/icons-material/Search";

const ClientsTable = ({ clients }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [search, setSearch] = useState("");
  const [selectedEstado, setSelectedEstado] = useState("Todos");
  const [selectedDepartamento, setSelectedDepartamento] = useState("Todos");
  const [selectedClient, setSelectedClient] = useState(null); // Cliente seleccionado
  const [open, setOpen] = useState(false); // Estado del modal

  // Obtener valores únicos para los filtros
  const uniqueEstados = ["Todos", ...new Set(clients.map((client) => client.estado))];
  const uniqueDepartamentos = ["Todos", ...new Set(clients.map((client) => client.departamento))];

  // Filtrar clientes
  const filteredClients = useMemo(() => {
    return clients
      .filter((client) =>
        (client.razon_social?.toLowerCase() || "").includes(search.toLowerCase()) ||
        (client.correo_ofi?.toLowerCase() || "").includes(search.toLowerCase()) ||
        (client.telefono?.toLowerCase() || "").includes(search.toLowerCase()) ||
        (client.cod_cliente?.toString() || "").includes(search.toLowerCase())
      )
      .filter((client) => 
        (selectedEstado === "Todos" || client.estado === selectedEstado) &&
        (selectedDepartamento === "Todos" || client.departamento === selectedDepartamento)
      ); 
  }, [clients, search, selectedEstado, selectedDepartamento]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  // Manejo de eventos
  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleEstadoChange = (event) => {
    setSelectedEstado(event.target.value);
    setPage(0);
  };

  const handleDepartamentoChange = (event) => {
    setSelectedDepartamento(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Mostrar detalles del cliente
  const handleOpenDetail = (client) => {
    setSelectedClient(client);
    setOpen(true);
  };

  // Cerrar modal
  const handleClose = () => {
    setOpen(false);
    setSelectedClient(null);
  };

  return (
    <Paper sx={{ maxWidth: "1000px", margin: "auto", padding: "16px", borderRadius: 2, boxShadow: 3 }}>
      {/* 🔍 Buscador y Filtros */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
        <TextField
          label="Buscar cliente..."
          variant="outlined"
          fullWidth
          value={search}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="select-estado" shrink>Estado</InputLabel>
          <Select 
            id="select-estados" 
            labelId="select-estado"
            value={selectedEstado} 
            label="Estado"
            onChange={handleEstadoChange}
            displayEmpty
          >
            {uniqueEstados.map((estado) => (
              <MenuItem key={estado} value={estado}>
                {estado}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel id="select-departamento" shrink>Departamento</InputLabel>
          <Select 
            id="select-departamentos"
            labelId="select-departamento"
            value={selectedDepartamento} 
            label="Departamento"
            onChange={handleDepartamentoChange}
            displayEmpty
          >
            {uniqueDepartamentos.map((departamento) => (
              <MenuItem key={departamento} value={departamento}>
                {departamento}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <TableContainer>
        <Table size="small">
          <TableHead >
            <TableRow>
              {["Cod", "Nombre", "Email", "Teléfono", "Estado", "Departamento", "Acciones"].map((header) => (
                <StyledTableCell>
                  {header}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((client, index) => (
              <TableRow
                key={client.id || client.cod_cliente}
              >
                <TableCell sx={{ padding: "8px" }}>{client.cod_cliente}</TableCell>
                <TableCell sx={{ padding: "8px" }}>{client.razon_social}</TableCell>
                <TableCell sx={{ padding: "8px" }}>{client.correo_ofi}</TableCell>
                <TableCell sx={{ padding: "8px" }}>{client.telefono}</TableCell>
                <TableCell sx={{ padding: "8px" }}>{client.estado}</TableCell>
                <TableCell sx={{ padding: "8px" }}>{client.departamento}</TableCell>
                <TableCell sx={{ padding: "8px" }}>
                  <Button variant="contained" size="small" onClick={() => handleOpenDetail(client)}>
                    Ver Detalle
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 📌 Paginación optimizada */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredClients.length}
        rowsPerPage={rowsPerPage}
        page={Math.min(page, Math.max(0, Math.ceil(filteredClients.length / rowsPerPage) - 1))}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* 🔍 Modal para ver detalles del cliente */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Detalles del Cliente</DialogTitle>
        <DialogContent>
          {selectedClient && (
            <div>
              <p><strong>Código:</strong> {selectedClient.cod_cliente}</p>
              <p><strong>Nombre:</strong> {selectedClient.razon_social}</p>
              <p><strong>Email:</strong> {selectedClient.correo_ofi}</p>
              <p><strong>Teléfono:</strong> {selectedClient.telefono}</p>
              <p><strong>Estado:</strong> {selectedClient.estado}</p>
              <p><strong>Departamento:</strong> {selectedClient.departamento}</p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClientsTable;
